import { useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  DrawGeometryType,
  DrawingStyles,
  useDrawFromDialog,
  useDrawingContext,
} from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useViewMode, ViewMode } from '../components/ViewModeProvider';
import { useReplayContext } from '../modules/Replay/ReplayContext';

/**
 * A wrapper around useDrawFromDialog that handles ViewMode switching.
 *
 * This hook:
 * 1. Sets ViewMode.Drawing when drawing starts
 * 2. Returns to ViewMode.Normal when drawing ends
 * 3. Stops replay if active when drawing starts
 *
 * @param form - React Hook Form instance
 * @param setShowDialog - Function to show/hide the parent dialog
 * @param geometryField - Form field name for geometry (default: 'geometry')
 * @param drawingStyle - Optional drawing style preset
 */
export const useDrawWithViewMode = (
  form: UseFormReturn<any>,
  setShowDialog: (showDialog: boolean) => void,
  geometryField = 'geometry',
  drawingStyle?: DrawingStyles
) => {
  const { showDrawPanel } = useDrawingContext();
  const { setViewMode, viewMode } = useViewMode();
  const { resetReplay } = useReplayContext();

  const {
    onStartDrawingFromDialog: baseOnStartDrawing,
    onAcceptDrawingFromDialog,
    onCancelDrawingFromDialog,
  } = useDrawFromDialog(form, setShowDialog, geometryField, drawingStyle);

  // Return to Normal mode when drawing ends
  useEffect(() => {
    if (!showDrawPanel && viewMode === ViewMode.Drawing) {
      setViewMode(ViewMode.Normal);
    }
  }, [showDrawPanel, viewMode, setViewMode]);

  // Wrapper that sets ViewMode.Drawing before starting
  const onStartDrawingFromDialog = useCallback(
    (
      geoType: DrawGeometryType,
      existingShape?: GeoJson | null,
      maxPoints?: number
    ) => {
      // Stop replay if active
      if (viewMode === ViewMode.Replay) {
        resetReplay({ resetDate: true });
      }
      // Switch to Drawing mode
      setViewMode(ViewMode.Drawing);
      // Start the drawing
      baseOnStartDrawing(geoType, existingShape, maxPoints);
    },
    [viewMode, resetReplay, setViewMode, baseOnStartDrawing]
  );

  return {
    onStartDrawingFromDialog,
    onAcceptDrawingFromDialog,
    onCancelDrawingFromDialog,
  };
};
